import {DefineTemplateRequest, ProcessBuilderPage, Template, Variable,} from '@/scripts/types/generated';
import {Page} from '@inertiajs/core';
import {traverseSchemaAsync} from '@/scripts/utils/form-builder.ts';
import {useVariables} from '@/scripts/composables/use-variables.ts';

const templates = ref<Template[]>();
const isLoading = ref<boolean>(false);

export const useTemplates = () => {
	const {getVariable} = useVariables();

	const refreshTemplates = (page: Page<ProcessBuilderPage>) => {
		templates.value = page.props.templates;
	};

	const initTemplates = async () => {
		while (isLoading.value) {
			await new Promise((resolve) => setTimeout(resolve, 100));
		}

		if (templates.value) return;

		isLoading.value = true;
		const page = await sendReloadRequest<ProcessBuilderPage>(['templates']);
		refreshTemplates(page);
		isLoading.value = false;
	};

	const createTemplate = async (
		request: DefineTemplateRequest
	): Promise<Template> => {
		// TODO have a look into unhandled error some day
		const page = await sendCreateRequest<ProcessBuilderPage>(
			route('tenant.templates.store'),
			request,
			['templates']
		);

		return new Promise<Template>((resolve) => {
			refreshTemplates(page);
			const newTemplate = templates.value.find(
				(template) => template.name === request.name
			);
			resolve(newTemplate);
		});
	};

	const deleteTemplate = async (slug: string): Promise<void> => {
		const page = await sendDeleteRequest<ProcessBuilderPage>(
			route('tenant.templates.destroy', {
				automation: slug,
			}),
			['templates']
		);

		return new Promise<void>((resolve) => {
			refreshTemplates(page);
			resolve();
		});
	};

	const getTemplateVariables = async (
		template: Template
	): Promise<Variable[]> => {
		const variables: Variable[] = [];
		await traverseSchemaAsync(template, async (element) => {
			if (
				'variableVersionUuid' in element &&
				element.variableVersionUuid
			) {
				variables.push(await getVariable(element.variableVersionUuid));
			}
		});

		return variables;
	};

	const setTemplates = (newTemplates: Template[]) => {
		templates.value = newTemplates;
	};

	return {
		templates,
		initTemplates,
		createTemplate,
		deleteTemplate,
		getTemplateVariables,
		setTemplates,
	};
};
